import { Trans } from '@lingui/react/macro';
import { Download, ImageOff, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSimulationVideo } from '@/api/hooks';
import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export const SimulationVideoCard = ({
  projectId,
  videoRef,
  onTimeUpdate,
}: {
  projectId: string;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  onTimeUpdate: (time: number) => void;
}): React.ReactElement => {
  const { data: blob, isLoading, isError } = useSimulationVideo(projectId);

  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    setVideoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  return (
    <Card className="pb-0">
      <CardHeader>
        <CardTitle>
          <Trans>Simulation video</Trans>
        </CardTitle>
        {videoUrl && (
          <CardAction>
            <Button
              variant="ghost"
              size="sm"
              className="!px-2 !py-1 text-sm"
              render={
                <a href={videoUrl} download={`simulation-${projectId}.mp4`}>
                  <Download data-icon="inline-start" /> <Trans>Download</Trans>
                </a>
              }
            />
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {isLoading && (
          <div className="flex h-80 flex-col items-center justify-center gap-3 text-slate-400">
            <Loader2 size={14} className="animate-spin" />
            <span className="text-sm">
              <Trans>Loading…</Trans>
            </span>
          </div>
        )}
        {isError && (
          <div className="flex h-80 flex-col items-center justify-center gap-3 text-slate-400">
            <ImageOff size={16} />
            <span className="text-sm">
              <Trans>Video not available</Trans>
            </span>
          </div>
        )}
        {videoUrl && (
          <video
            ref={videoRef}
            src={videoUrl}
            controls={true}
            className="block w-full max-h-[720px]"
            onTimeUpdate={(e) => onTimeUpdate(e.currentTarget.currentTime)}
          >
            <track kind="captions" src="" label="" />
          </video>
        )}
      </CardContent>
    </Card>
  );
};
